import {
  ChangeDetectionStrategy,
  Component,
  effect,
  EventEmitter,
  input,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { Experiences } from '@local/client-contracts';
import { debounceTime, distinctUntilChanged, Subject, take } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ExperienceSearchItem } from 'src/app/bar/views/results';
import { AssistantsConstants, isGeneralAssistant } from '@local/common-web';
import { PopupRef, PopupService, UTextareaInteractiveComponent } from '@local/ui-infra';
import {
  AdvancedInstructionsPopupComponent,
  AdvancedInstructionsPopupData,
} from '../advanced-instructions-popup/advanced-instructions-popup.component';
import { InstructionDropdownItem, InstructionsDynamicContent, InstructionsMode } from '../../models/assistant-instructions.model';
import { ASSISTANT_INSTRUCTIONS_CONTENT } from '../../models/assistant-instructions-content';

@UntilDestroy()
@Component({
  selector: 'assistant-instructions',
  templateUrl: './assistant-instructions.component.html',
  styleUrl: './assistant-instructions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssistantInstructionsComponent implements OnInit {
  private readonly CUSTOM_QUERY_DEBOUNCE = 500;
  private readonly DEFAULT_MAX_CHARACTERS = 8000;
  private readonly EXTENDED_MAX_CHARACTERS = 32000;
  readonly ITEM_ICON_STYLE = { fontSize: '14px', marginRight: '8px' };
  readonly DROPDOWN_STYLE = { width: '216px' };
  readonly ASSISTANT_INSTRUCTIONS_CONTENT = ASSISTANT_INSTRUCTIONS_CONTENT;
  private currentInstructions: Experiences.ExperienceInstructions;
  private customQueryChanged$ = new Subject<string>();
  private advancedEditPopupRef: PopupRef<AdvancedInstructionsPopupComponent, AdvancedInstructionsPopupData>;
  private experienceType: Experiences.ExperienceType;
  selectedResponseLength: InstructionDropdownItem<Experiences.ResponseLength>;
  selectedAdjustTone: InstructionDropdownItem<Experiences.AdjustTone>;
  customQuery: WritableSignal<string> = signal('');
  instructionsMode: WritableSignal<InstructionsMode> = signal('default');
  displayInstructions: WritableSignal<Set<Experiences.DefaultInstructionType>> = signal(new Set());
  maxCharactersCustomQuery = signal<number>(this.DEFAULT_MAX_CHARACTERS);

  assistant = input<Experiences.ExperienceItem>();
  @Input() disabled = false;
  @Input() styleSize: 'regular' | 'expanded' = 'regular';
  @Input() set originalAssistant(val: ExperienceSearchItem) {
    this.experienceType = val.experienceType;
    const type: Experiences.ExperienceType = this.experienceType;
    this.displayInstructions.set(AssistantsConstants.INSTRUCTIONS_PER_TYPE[type]);
    this.initData(val.settings?.instructions);
  }
  @Input() dynamicContent: InstructionsDynamicContent = {
    advancedTitle: "Provide clear instructions to customize the bot's response.",
    advancedPlaceholder:
      'You are a customer support agent named John Doe. Respond to customer inquiries with politeness, empathy, and attention to detail. Ensure responses are clear, complete, and address all parts of the inquiry. If clarification is needed, ask for it respectfully. Always maintain a friendly, professional tone. End each message with:\n“Best regards,\nJohn Doe”',
  };
  @Output() updateInstructions = new EventEmitter<{ instructions: Experiences.ExperienceInstructions }>();
  @ViewChild(UTextareaInteractiveComponent) uTextareaInteractive: UTextareaInteractiveComponent;

  constructor(private popupService: PopupService) {
    effect(
      () => {
        const assistant = this.assistant();
        if (isGeneralAssistant(assistant) && assistant.settings.general?.knowledgeType === 'External') {
          this.maxCharactersCustomQuery.set(this.EXTENDED_MAX_CHARACTERS);
          return;
        }
        this.maxCharactersCustomQuery.set(this.DEFAULT_MAX_CHARACTERS);
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        if (this.customQuery()?.length > this.maxCharactersCustomQuery()) {
          this.customQuery.set(this.customQuery().slice(0, this.maxCharactersCustomQuery()));
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit() {
    this.customQueryChanged$
      .pipe(debounceTime(this.CUSTOM_QUERY_DEBOUNCE), distinctUntilChanged(), untilDestroyed(this))
      .subscribe((value: string) => {
        this.updateCustomQuery(value);
      });
  }

  changeMode() {
    const newMode = this.instructionsMode() === 'advanced' ? 'default' : 'advanced';
    this.instructionsMode.set(newMode);
    this.updateCurrentInstructions();
  }

  onDropDownChanged(
    $event: InstructionDropdownItem<Experiences.BotSensitivity | Experiences.AdjustTone | Experiences.ResponseLength>,
    preferencePropType: Experiences.DefaultInstructionType
  ) {
    if (this.instructionsMode() === 'advanced') {
      return;
    }
    this.handlePreference(preferencePropType, $event.value);
    this.updateCurrentInstructions();
  }

  onCustomQueryChange($event: string) {
    this.customQueryChanged$.next($event);
  }

  openLearnMore() {
    const url = this.dynamicContent.learnMoreUrl || this.ASSISTANT_INSTRUCTIONS_CONTENT.learnMoreUrl[this.experienceType];
    window.open(url, '_blank');
  }

  openAdvancePopup() {
    if (this.advancedEditPopupRef) {
      this.advancedEditPopupRef.destroy();
    }
    this.advancedEditPopupRef = this.popupService.open<AdvancedInstructionsPopupComponent, AdvancedInstructionsPopupData>(
      'center',
      AdvancedInstructionsPopupComponent,
      {
        footer: this.ASSISTANT_INSTRUCTIONS_CONTENT.advancedQueryWarning,
        currentText: this.customQuery() || '',
        placeholder: this.dynamicContent?.advancedPlaceholder,
        maxLength: this.maxCharactersCustomQuery(),
        disabled: this.disabled,
      },
      {
        position: 'center',
        backdropStyle: 'blur-2',
        closeOnClickOut: true,
      }
    );
    this.advancedEditPopupRef.destroy$.pipe(untilDestroyed(this), take(1)).subscribe(() => {
      this.advancedEditPopupRef = null;
    });
    this.advancedEditPopupRef.compInstance.updateText.pipe(untilDestroyed(this)).subscribe((value) => {
      this.updateCustomQuery(value);
      this.uTextareaInteractive?.setFocus();
    });
  }

  private updateCustomQuery(text: string) {
    this.customQuery.set(text);
    this.updateCurrentInstructions();
  }

  private isInstructionAvailable(type: Experiences.DefaultInstructionType): boolean {
    return this.displayInstructions()?.has(type);
  }

  private initData(instructions: Experiences.ExperienceInstructions) {
    this.currentInstructions = instructions;
    const mode: InstructionsMode = this.currentInstructions?.customInstructions ? 'advanced' : 'default';
    this.instructionsMode.set(mode);
    if (this.isInstructionAvailable('answerDetailLevel')) {
      this.configResponseLength();
    }

    if (this.isInstructionAvailable('answerTone')) {
      this.configAdjustTone();
    }
    this.customQuery.set(this.currentInstructions?.customInstructions);
  }

  private configResponseLength() {
    const defaultResponseLength = AssistantsConstants.DEFAULT_RESPONSE_LENGTH_PROPERTY;
    const answerDetailLevel: Experiences.ResponseLength = this.currentInstructions?.answerDetailLevel
      ? this.currentInstructions.answerDetailLevel
      : defaultResponseLength;
    this.setSelectedResponseLength(answerDetailLevel);
  }

  private configAdjustTone() {
    const defaultAdjustTone = AssistantsConstants.DEFAULT_ADJUST_TONE_PROPERTY;
    const adjustTone: Experiences.AdjustTone = this.currentInstructions?.answerTone
      ? this.currentInstructions.answerTone
      : defaultAdjustTone;
    this.setSelectedAdjustTone(adjustTone);
  }

  private setSelectedResponseLength(answerDetailLevelValue: Experiences.ResponseLength) {
    this.selectedResponseLength = this.ASSISTANT_INSTRUCTIONS_CONTENT.optionsResponseLength.find(
      (option) => option.value === answerDetailLevelValue
    );
  }

  private setSelectedAdjustTone(adjustToneValue: Experiences.AdjustTone) {
    this.selectedAdjustTone = this.ASSISTANT_INSTRUCTIONS_CONTENT.optionsAdjustTone.find((option) => option.value === adjustToneValue);
  }

  private handlePreference(preferencePropType: Experiences.DefaultInstructionType, value: any) {
    switch (preferencePropType) {
      case 'answerDetailLevel':
        this.setSelectedResponseLength(value);
        break;
      case 'answerTone':
        this.setSelectedAdjustTone(value);
        break;
    }
  }

  private updateCurrentInstructions() {
    const updatedInstructions: Experiences.ExperienceInstructions = {};
    const isDefault = this.instructionsMode() === 'default' || !this.customQuery();
    if (isDefault) {
      if (this.isInstructionAvailable('answerDetailLevel') && this.selectedResponseLength?.value) {
        updatedInstructions.answerDetailLevel = this.selectedResponseLength.value;
      }
      if (this.isInstructionAvailable('answerTone') && this.selectedAdjustTone?.value) {
        updatedInstructions.answerTone = this.selectedAdjustTone.value;
      }
    } else {
      updatedInstructions.customInstructions = this.customQuery() || '';
    }
    if (this.currentInstructions?.sensitivity) {
      updatedInstructions.sensitivity = this.currentInstructions.sensitivity;
    }
    this.currentInstructions = updatedInstructions;
    this.updateInstructions.emit({ instructions: this.currentInstructions });
  }
}
